import type {
  IConnectCustomIntegrationParams,
  ICreateCustomIntegrationBody,
  ICustomIntegration,
  ICustomIntegrationTestResult,
  ICustomIntegrationTool,
  ICustomIntegrationUserConnection,
  IDisconnectCustomIntegrationParams,
  IOpenApiParsedResult,
  IParseOpenApiSpecBody,
  IUpdateCustomIntegrationParams,
} from "../types";
import { encodePathSegment } from "../utils/encode-path-segment";
import type { ApiClient } from "./client";

export class CustomIntegrationApi {
  constructor(private apiClient: ApiClient) {}

  async list(): Promise<ICustomIntegration[]> {
    const response = await this.apiClient.get<ICustomIntegration[]>(
      "/custom-integration",
    );
    return response.data;
  }

  async getOne(uuid: string): Promise<ICustomIntegration> {
    const response = await this.apiClient.get<ICustomIntegration>(
      `/custom-integration/${encodePathSegment(uuid)}`,
    );
    return response.data;
  }

  async create(
    body: ICreateCustomIntegrationBody,
  ): Promise<ICustomIntegration> {
    const response = await this.apiClient.post<ICustomIntegration>(
      "/custom-integration",
      body,
    );
    return response.data;
  }

  async update(
    params: IUpdateCustomIntegrationParams,
  ): Promise<ICustomIntegration> {
    const response = await this.apiClient.patch<ICustomIntegration>(
      `/custom-integration/${encodePathSegment(params.uuid)}`,
      params.body,
    );
    return response.data;
  }

  async remove(uuid: string): Promise<void> {
    await this.apiClient.delete(
      `/custom-integration/${encodePathSegment(uuid)}`,
    );
  }

  async testConnection(
    uuid: string,
    authCredential?: string | null,
  ): Promise<ICustomIntegrationTestResult> {
    const response = await this.apiClient.post<ICustomIntegrationTestResult>(
      `/custom-integration/${encodePathSegment(uuid)}/test`,
      authCredential !== undefined ? { authCredential } : {},
    );
    return response.data;
  }

  async listTools(integrationUuid: string): Promise<ICustomIntegrationTool[]> {
    const response = await this.apiClient.get<ICustomIntegrationTool[]>(
      `/custom-integration/${encodePathSegment(integrationUuid)}/tools`,
    );
    return response.data;
  }

  async parseOpenApiSpec(
    body: IParseOpenApiSpecBody,
  ): Promise<IOpenApiParsedResult> {
    const response = await this.apiClient.post<IOpenApiParsedResult>(
      "/custom-integration/parse-openapi",
      body,
    );
    return response.data;
  }

  async connect(
    params: IConnectCustomIntegrationParams,
  ): Promise<ICustomIntegrationUserConnection> {
    const response =
      await this.apiClient.post<ICustomIntegrationUserConnection>(
        `/custom-integration/${encodePathSegment(params.integrationUuid)}/connect`,
        params.body,
      );
    return response.data;
  }

  async disconnect(params: IDisconnectCustomIntegrationParams): Promise<void> {
    await this.apiClient.delete(
      `/custom-integration/${encodePathSegment(params.integrationUuid)}/connect`,
    );
  }
}
